import * as React from 'react'
import moment from 'moment'
import FlexView, { FlexViewProps } from '@/modules/shared/components/FlexView'

import { DriverCalendarDayModel } from 'app/models/calendar/driverCalendarDayModel'
import { ActivitySource } from 'app/constants/activitySource'
import { MergedActivitySource } from 'app/constants/mergedActivitySource'
import { TmsType } from 'app/constants/tmsType'
import { getActivitySource } from '../../../../../helpers/ActivitySourceResolver'
import { faArrowRight, faClose, FaIcon, faPencilSolid } from '@/lib/fa'
import Tooltip from '@/modules/drivers/shared/ToolTip'
import { DayType } from '@/app/constants/dayType'
import { DayTypePaletteId, getDayTypeVisual } from './palettes/dayTypePalettes'

export const COLORS = {
  Violation: '#FF6A88',
  Gps: '#61A0DD',
  TmsLoading: '#FF6A00',
  TmsUnloading: '#0094FF',
  CardReading: '#6BBE4F',
  Notes: '#2B2E33',
  ContractStartDate: '#000000',
  ContractEndDate: '#C0C0C0',
  AnnexDate: '#7F776A',
  driverCardActivity: '#1EAF4A',
  tachographActivity: '#404040',
  vehicleSourceOnly: '#9333EA',
  tachographEvents: '#0D9488',
  noActivity: '#ED2229',
  analogTachographActivity: '#F7DC06',
  attestation: '#FFA500',
  attestationDay: '#2c57ff',
  rest: '#C3DEE8',
  file: '#FFA500',
}

const NO_DATA_STRIPES =
  'repeating-linear-gradient(45deg, var(--bd-stripe-a), var(--bd-stripe-a) 4px, var(--bd-stripe-b) 4px, var(--bd-stripe-b) 8px)'

const getActivityColor = (source: ActivitySource, defaultColor: string) => {
  switch (source) {
    case ActivitySource.DRIVER:
    case ActivitySource.TACHOGRAPH:
      return defaultColor
    case ActivitySource.USER:
      return COLORS.analogTachographActivity
    case ActivitySource.ANALOG:
      return COLORS.analogTachographActivity
    case ActivitySource.REST:
      return COLORS.rest
    case ActivitySource.FILE:
      return COLORS.file
    case ActivitySource.NO_DATA:
      return NO_DATA_STRIPES
    default:
      return 'transparent'
  }
}

export type CalendarDayCellProps = Omit<
  FlexViewProps,
  'height' | 'width' | 'style' | 'className' | 'children' | 'column'
> & {
  day: DriverCalendarDayModel
  height: number
  width: number
  paletteId: DayTypePaletteId
  showViolations?: boolean
  showTrips?: boolean
  showTmsData?: boolean
  isSelected?: boolean
  isMultiSelected?: boolean
  multiSelectMode?: boolean
  isHovered?: boolean
  isFirstRow?: boolean
  isLastInRow?: boolean
}

/**
 * Wygląd jednego dnia kalendarza rocznego, bez routera i store'ów - ten sam
 * komponent rysuje widok w aplikacji i wydruk w chart-rendererze.
 */
export const CalendarDayCell: React.FC<CalendarDayCellProps> = React.memo(
  (props) => {
    const {
      day,
      height,
      width,
      paletteId,
      showViolations = true,
      showTrips = false,
      showTmsData = false,
      isSelected = false,
      isMultiSelected = false,
      multiSelectMode,
      isHovered = false,
      isFirstRow = false,
      isLastInRow = false,
      ...domProps
    } = props
    const availableBarWidth = width - 2
    const totalCardCount = (day.mergedCardAcitivities || []).reduce(
      (sum, v) => sum + v.count,
      0,
    )
    const totalTachCount = (day.mergedTachographAcitivities || []).reduce(
      (sum, v) => sum + v.count,
      0,
    )
    const cardGapWidth =
      totalCardCount > 0 ? availableBarWidth / totalCardCount : 1.4
    const tachGapWidth =
      totalTachCount > 0 ? availableBarWidth / totalTachCount : 1.4
    const dayTypeVisual = React.useMemo(
      () => getDayTypeVisual(paletteId, day.dayType),
      [paletteId, day.dayType],
    )

    const isWeekendOrHoliday = React.useMemo(() => {
      return (
        day != null &&
        (day.isHoliday || moment(day.activityDayLocalTimeDate).day() == 0)
      )
    }, [day])
    const isWeekend = React.useMemo(() => {
      return (
        day != null &&
        (moment(day.activityDayLocalTimeDate).day() == 0 ||
          moment(day.activityDayLocalTimeDate).day() == 6)
      )
    }, [day])
    const cellColor = isWeekend ? '#F1F4FF' : '#FFFFFF'

    const isAllNoData = React.useMemo(() => {
      const cardActivities = day.mergedCardAcitivities || []
      const tachActivities = day.mergedTachographAcitivities || []
      if (cardActivities.length === 0 && tachActivities.length === 0)
        return false
      return (
        cardActivities.every(
          (v) => getActivitySource(v.source) === ActivitySource.NO_DATA,
        ) &&
        tachActivities.every(
          (v) => getActivitySource(v.source) === ActivitySource.NO_DATA,
        )
      )
    }, [day])

    const shouldShowBD = React.useMemo(() => {
      if (!isAllNoData) return false
      const today = moment().startOf('day')
      const dayDate = moment(day.activityDayLocalTimeDate).startOf('day')
      return dayDate.isBefore(today)
    }, [isAllNoData, day.activityDayLocalTimeDate])

    const hasActivityData = React.useMemo(() => {
      if (day.dayNumber === 0) return false

      if (showTrips) {
        return (day.tripData || []).some((v) => v.count > 0)
      }

      const hasRealActivity = (activities?: Array<MergedActivitySource>) =>
        (activities || []).some(
          (v) =>
            v.count > 0 &&
            getActivitySource(v.source) !== ActivitySource.NO_DATA,
        )

      return (
        hasRealActivity(day.mergedCardAcitivities) ||
        hasRealActivity(day.mergedTachographAcitivities)
      )
    }, [day, showTrips])

    const shouldShowActivities = React.useMemo(() => {
      const today = moment().startOf('day')
      const dayDate = moment(day.activityDayLocalTimeDate).startOf('day')
      return dayDate.isBefore(today) || hasActivityData
    }, [day.activityDayLocalTimeDate, hasActivityData])

    const isFutureDay = React.useMemo(() => {
      const today = moment().startOf('day')
      const dayDate = moment(day.activityDayLocalTimeDate).startOf('day')
      return dayDate.isSameOrAfter(today)
    }, [day.activityDayLocalTimeDate])

    const isToday = React.useMemo(() => {
      const today = moment().startOf('day')
      const dayDate = moment(day.activityDayLocalTimeDate).startOf('day')
      return dayDate.isSame(today)
    }, [day.activityDayLocalTimeDate])

    const isEmpty = day.dayNumber === 0
    const hasAttestation = day.hasAttestation || (day.attestationType ?? 0) > 0

    const hasContractMarker =
      !isEmpty &&
      (day.isContractStartDate || day.isAnnexDate || day.isContractEndDate)
    const hasTachoMarker = day?.tachographReadEvent || day?.driverCardReadEvent
    const hasAnyMarker = hasContractMarker || hasTachoMarker

    return (
      <FlexView
        {...domProps}
        height={height}
        column
        hAlignContent="center"
        vAlignContent="top"
        style={{
          width: width,
          borderStyle: 'solid',
          borderLeftWidth: isEmpty || isFutureDay ? 0 : 1,
          borderTopWidth: isFirstRow ? 1 : 0,
          borderBottomWidth: 1,
          borderRightWidth: isLastInRow ? 1 : isEmpty || isFutureDay ? 0 : 1,

          borderColor: 'var(--color-border-base-300, #e5e7eb)',
          background: isEmpty ? '#fff' : cellColor,
          cursor: isEmpty ? 'default' : 'pointer',
          position: 'relative',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          zIndex:
            isSelected || isMultiSelected
              ? 50
              : isHovered
                ? 40
                : hasAnyMarker
                  ? 20
                  : 'auto',
          overflow: hasAnyMarker ? 'visible' : 'hidden',
        }}
        className={
          isEmpty
            ? 'day-item-empty'
            : multiSelectMode
              ? `day-item-multi-selection tw-group${isSelected || isMultiSelected ? 'tw-z-50 tw-ring-2 tw-ring-primary' : ''}`
              : `day-item tw-group${isSelected || isMultiSelected ? 'tw-z-50 tw-ring-2 tw-ring-primary' : ''}`
        }
      >
        {!isEmpty && (
          <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-z-10 tw-bg-primary/20 tw-opacity-0 tw-transition-opacity group-hover:tw-opacity-100" />
        )}
        {!isEmpty && hasAttestation && (
          <div
            className="tw-pointer-events-none tw-absolute tw-inset-0 tw-z-20"
            style={{ border: `2px solid ${COLORS.attestationDay}` }}
          />
        )}
        {!isEmpty && !isFutureDay && (isSelected || isMultiSelected) && (
          <div className="tw-absolute tw-inset-0 tw-z-10 tw-rounded-sm tw-bg-[#0043FF]/15" />
        )}
        <div className="tw-relative tw-flex tw-w-full tw-flex-1 tw-flex-col tw-justify-between">
          <div
            className={`tw-flex tw-w-full tw-justify-between tw-px-1 tw-pt-[2px] ${isToday ? 'tw-rounded tw-bg-[#2c57ff]' : ''}`}
          >
            <span
              className={`tw-text-xs tw-font-medium ${isToday ? 'tw-text-white' : isWeekendOrHoliday ? 'tw-text-red-500' : 'tw-text-content-base'}`}
            >
              {day.dayNumber !== 0 ? day.dayNumber : ''}
            </span>
          </div>
          <div
            className="tw-relative tw-pl-1 tw-text-xs tw-font-medium"
            style={{
              background:
                day.dayType !== DayType.NONE
                  ? dayTypeVisual.background
                  : undefined,
              borderLeft:
                day.dayType !== DayType.NONE
                  ? `${dayTypeVisual.borderWidth}px solid ${dayTypeVisual.border}`
                  : undefined,

              color:
                day.dayType !== DayType.NONE ? dayTypeVisual.text : undefined,
              fontWeight: paletteId === 'classic' ? undefined : 600,
            }}
          >
            {day.dayType !== DayType.NONE ? DayType[day.dayType] : ''}
            <div className="tw-absolute -tw-bottom-[9px] tw-left-0 tw-z-10 tw-flex tw-w-full tw-items-center tw-justify-between tw-px-[1px] tw-py-0.5">
              {[
                {
                  condition: showViolations && day.violationCount !== 0,
                  color: COLORS.Violation,
                },
                { condition: day.hasGpsData, color: COLORS.Gps },
                {
                  condition:
                    showTmsData &&
                    day.hasTmsData &&
                    (day.tmsType === TmsType.Both ||
                      day.tmsType === TmsType.Loading),
                  color: COLORS.TmsLoading,
                },
                {
                  condition:
                    showTmsData &&
                    day.hasTmsData &&
                    (day.tmsType === TmsType.Both ||
                      day.tmsType === TmsType.Unloading),
                  color: COLORS.TmsUnloading,
                },
                {
                  condition: day.hasVehicleSourceOnlyActivity,
                  color: COLORS.vehicleSourceOnly,
                },
                {
                  condition: day.hasTachographEvents,
                  color: COLORS.tachographEvents,
                },
              ].map(({ condition, color }, idx) => (
                <div
                  key={idx}
                  style={{
                    height: 6,
                    width: 6,
                    background: condition ? color : 'transparent',
                    borderRadius: 3,
                  }}
                />
              ))}
            </div>
          </div>

          <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-1">
            {shouldShowBD ? (
              <div
                className="tw-flex tw-flex-1 tw-items-end tw-justify-start tw-text-center tw-text-[10px] tw-font-medium tw-text-red-500"
                style={{ background: NO_DATA_STRIPES }}
              >
                <span className="tw-text-xs tw-font-semibold tw-text-neutral">
                  BD
                </span>
              </div>
            ) : shouldShowActivities ? (
              <div className="tw-flex tw-flex-1 tw-flex-col tw-justify-end tw-gap-1 tw-pt-1.5">
                {showTrips ? (
                  (() => {
                    const trips = day.tripData || []
                    const totalTripCount = trips.reduce(
                      (sum: number, v: any) => sum + v.count,
                      0,
                    )
                    const tripGapWidth =
                      totalTripCount > 0
                        ? availableBarWidth / totalTripCount
                        : 1.4
                    return (
                      <FlexView className="tw-pb-1.5">
                        {trips.map((value: any, index: number) => (
                          <span
                            key={index}
                            style={{
                              height: 8,
                              width: tripGapWidth * value.count,
                              background:
                                value.source === 1 ? '#B200FC' : '#C0C0C0',
                            }}
                          />
                        ))}
                      </FlexView>
                    )
                  })()
                ) : (
                  <>
                    <FlexView>
                      {(day.mergedCardAcitivities || []).map(
                        (value: MergedActivitySource, index: number) => {
                          return (
                            <span
                              key={index}
                              style={{
                                height: 8,
                                width: cardGapWidth * value.count,
                                background: getActivityColor(
                                  getActivitySource(value.source),
                                  COLORS.driverCardActivity,
                                ),
                              }}
                            />
                          )
                        },
                      )}
                    </FlexView>
                    <FlexView>
                      {(day.mergedTachographAcitivities || []).map(
                        (value: MergedActivitySource, index: number) => (
                          <span
                            key={index}
                            style={{
                              height: 8,
                              width: tachGapWidth * value.count,
                              background: getActivityColor(
                                getActivitySource(value.source),
                                COLORS.tachographActivity,
                              ),
                            }}
                          />
                        ),
                      )}
                    </FlexView>
                  </>
                )}
              </div>
            ) : null}
          </div>
        </div>

        {!isEmpty && day.isAnnexDate && (
          <div className="tw-absolute tw-bottom-0 tw-left-0 tw-z-30 tw-flex tw-h-full tw-w-0.5 tw-items-center tw-justify-center tw-overflow-visible tw-bg-[#2c57ff]">
            <Tooltip
              portal
              text="Aneks do umowy"
              className="tw-grid tw-h-4 tw-w-4 tw-flex-shrink-0 tw-place-items-center tw-rounded-full tw-bg-primary"
            >
              <FaIcon
                icon={faArrowRight}
                className="tw-h-[10px] tw-w-[10px] tw-text-white"
              />
            </Tooltip>
          </div>
        )}
        {!isEmpty && day.isContractEndDate && (
          <div className="tw-absolute tw-bottom-0 tw-right-0 tw-z-30 tw-flex tw-h-full tw-w-0.5 tw-items-center tw-justify-center tw-overflow-visible tw-bg-[#2c57ff]">
            <Tooltip
              portal
              text="Zakończenie zatrudnienia"
              className="tw-grid tw-h-4 tw-w-4 tw-flex-shrink-0 tw-place-items-center tw-rounded-full tw-bg-primary"
            >
              <FaIcon
                icon={faClose}
                className="tw-h-[10px] tw-w-[10px] tw-text-white"
              />
            </Tooltip>
          </div>
        )}
        {!isEmpty && day.isContractStartDate && (
          <div className="tw-absolute tw-bottom-0 tw-left-0 tw-z-30 tw-flex tw-h-full tw-w-0.5 tw-items-center tw-justify-center tw-overflow-visible tw-bg-[#2c57ff]">
            <Tooltip
              portal
              text="Rozpoczęcie zatrudnienia"
              className="tw-grid tw-h-4 tw-w-4 tw-flex-shrink-0 tw-place-items-center tw-rounded-full tw-bg-primary"
            >
              <FaIcon
                icon={faPencilSolid}
                className="tw-h-[10px] tw-w-[10px] tw-text-white"
              />
            </Tooltip>
          </div>
        )}
        {isToday && (
          <div className="tw-absolute tw-bottom-0 tw-right-4 tw-h-full tw-w-0.5 tw-bg-[#2c57ff]" />
        )}
        {day.info != null && (
          <div
            className="triangle-topright"
            style={{ position: 'absolute', top: 0, right: 0, zIndex: 30 }}
          />
        )}
        {day.driverCardReadEvent && (
          <div
            className="triangle-topleft"
            style={{ position: 'absolute', top: 0, left: 0, zIndex: 30 }}
          />
        )}
        {day.tachographReadEvent && (
          <div
            className="triangle-topright"
            style={{ position: 'absolute', top: 0, right: 0, zIndex: 30 }}
          />
        )}
      </FlexView>
    )
  },
)
